import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ImpactReport.scss";

function FadeIn({ delay = 0, children, className = "" }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div className={`fade-in ${show ? "fade-in--visible" : ""} ${className}`}>
      {children}
    </div>
  );
}

function DonutChart({ title, total, segments }) {
  const radius = 36;
  const circ = 2 * Math.PI * radius;
  const opacities = [1, 0.5, 0.25];
  let offset = 0;
  const [numPart, unitPart] = total.split(" ");

  return (
    <div className="report-donut">
      <svg
        viewBox="0 0 100 100"
        width="110"
        height="110"
        style={{ flexShrink: 0 }}
      >
        {segments.map((seg, i) => {
          const dashLen = (seg.pct / 100) * circ;
          const dashGap = circ - dashLen;
          const cur = offset;
          offset += dashLen;
          return (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="white"
              strokeWidth="7"
              opacity={opacities[i] ?? 0.25}
              strokeDasharray={`${dashLen} ${dashGap}`}
              strokeDashoffset={-cur}
              strokeLinecap="butt"
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "50% 50%",
              }}
            />
          );
        })}
        <text
          textAnchor="middle"
          fill="white"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          <tspan
            x="49"
            y="46"
            dominantBaseline="central"
            style={{ fontSize: "0.75rem" }}
          >
            {numPart}
          </tspan>
          <tspan
            x="50"
            y="58"
            dominantBaseline="central"
            style={{ fontSize: "0.6rem", opacity: 0.6 }}
          >
            {unitPart}
          </tspan>
        </text>
      </svg>
      <div className="report-donut-legend">
        <p className="report-donut-title">{title}</p>
        {segments.map((seg, i) => (
          <p key={i} className="report-donut-legend-item">
            <span
              className="report-donut-dot"
              style={{ opacity: opacities[i] ?? 0.25 }}
            />
            {seg.label} ({seg.pct}%)
          </p>
        ))}
      </div>
    </div>
  );
}

function ScreenReceipt({ energyJ, onNext }) {
  const scrollRef = useRef(null);
  const electricity = (energyJ / 3600).toFixed(2);
  const electricityKJ = (energyJ / 1000).toFixed(1);
  const waterUse = (energyJ * 0.00047).toFixed(2);
  const carbonEmissions = (energyJ * 0.0001).toFixed(3);
  const hiddenTotal = energyJ * 1.56;

  return (
    <div className="report-screen" ref={scrollRef}>
      <div className="report-inner">
        <FadeIn delay={0}>
          <p className="report-label">01 / 06</p>
        </FadeIn>
        <FadeIn delay={100}>
          <h1 className="report-hero-title">Your Receipt</h1>
          <p className="report-hero-sub">
            One query. One answer. Here's what it cost.
          </p>
        </FadeIn>

        <div className="report-metric-row">
          <FadeIn delay={300} className="report-metric-cell">
            <div className="report-metric-card">
              <span className="report-metric-icon">⚡</span>
              <p className="report-metric-label">Energy</p>
              <p className="report-metric-value">{electricity} Wh</p>
              <p className="report-metric-sub">{electricityKJ} kJ</p>
            </div>
          </FadeIn>
          <FadeIn delay={420} className="report-metric-cell">
            <div className="report-metric-card">
              <span className="report-metric-icon">💧</span>
              <p className="report-metric-label">Water</p>
              <p className="report-metric-value">{waterUse} mL</p>
              <p className="report-metric-sub">evaporated</p>
            </div>
          </FadeIn>
          <FadeIn delay={540} className="report-metric-cell">
            <div className="report-metric-card">
              <span className="report-metric-icon">☁</span>
              <p className="report-metric-label">Carbon</p>
              <p className="report-metric-value">{carbonEmissions} g</p>
              <p className="report-metric-sub">CO₂</p>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={700}>
          <p className="report-section-label">Where the energy went</p>
          <div className="report-donuts-row">
            <DonutChart
              title="Direct"
              total={`${energyJ.toFixed(1)} J`}
              segments={[
                { label: "Inference", pct: 74 },
                { label: "Cooling", pct: 22 },
                { label: "Network", pct: 4 },
              ]}
            />
            <DonutChart
              title="Hidden"
              total={`${hiddenTotal.toFixed(1)} J`}
              segments={[
                { label: "Training & R&D", pct: 75 },
                { label: "Infrastructure", pct: 25 },
              ]}
            />
          </div>
        </FadeIn>

        <FadeIn delay={900}>
          <div className="report-footnote">
            <p>
              That water was used to cool the servers that processed your
              question. It evaporated. It's gone.
            </p>
          </div>
        </FadeIn>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          While you were cranking...
        </button>
      </div>
    </div>
  );
}

const QUERIES_PER_DAY = 1_000_000_000;
const TICK_MS = 100;
const INCREMENT = Math.round((QUERIES_PER_DAY / 86400) * (TICK_MS / 1000));

function ScreenRightNow({ onNext }) {
  const scrollRef = useRef(null);
  const [queries, setQueries] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setQueries((q) => q + INCREMENT), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const waterLiters = (queries * 0.0075).toFixed(1);

  return (
    <div className="report-screen" ref={scrollRef}>
      <div className="report-inner">
        <FadeIn delay={0}>
          <p className="report-label">02 / 06</p>
        </FadeIn>
        <FadeIn delay={100}>
          <h1 className="report-hero-title">Right Now</h1>
        </FadeIn>

        <div className="report-divider" />

        <FadeIn delay={300}>
          <div className="report-live-section">
            <p className="report-live-label">
              Queries since you opened this screen
            </p>
            <p className="report-live-count">{queries.toLocaleString()}</p>
            <p className="report-live-label">Water evaporated</p>
            <p className="report-live-water">{waterLiters} liters</p>
          </div>
        </FadeIn>

        <div className="report-divider" />

        <FadeIn delay={600}>
          <div className="report-facts-list">
            {[
              { val: "1 billion", desc: "AI queries processed today" },
              {
                val: "7.5M liters",
                desc: "of water evaporated today for AI cooling — enough to fill 3 Olympic swimming pools",
              },
              {
                val: "40M kWh",
                desc: "of electricity consumed today — more than some countries use in a week",
              },
              {
                val: "945 TWh",
                desc: "projected annual data center electricity by 2030 — more than Japan's total consumption",
              },
            ].map(({ val, desc }) => (
              <div key={val} className="report-fact-row">
                <span className="report-fact-value">{val}</span>
                <span className="report-fact-desc">{desc}</span>
              </div>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={900}>
          <p className="report-italic-quote">
            Your query was one in a billion today.
            <br />
            The water that cooled all of them is gone.
          </p>
        </FadeIn>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          Where does the water go?
        </button>
      </div>
    </div>
  );
}

const WATER_STATS = [
  {
    val: "4 billion",
    desc: "people face severe water scarcity at least one month per year",
  },
  {
    val: "$58 trillion",
    desc: "annual value of freshwater ecosystems — 60% of global GDP",
  },
  {
    val: "3 billion",
    desc: "people live in areas where water storage is declining",
  },
];

const WATER_BULLETS = [
  "Data center water consumption projected to grow 1,100% by 2028",
  "Over half of the world's largest data centers sit in regions already classified as medium to high water stress",
  "Texas data centers projected to use 399 billion gallons by 2030 — enough to draw Lake Mead down 16 feet",
  "During droughts, residents face water restrictions. The servers keep running.",
];

function ScreenWaterBankruptcy({ onNext }) {
  const scrollRef = useRef(null);

  return (
    <div className="report-screen" ref={scrollRef}>
      <div className="report-inner">
        <FadeIn delay={0}>
          <p className="report-label">03 / 06</p>
        </FadeIn>

        <FadeIn delay={100}>
          <h1 className="report-hero-title">Global Water Bankruptcy</h1>
          <p className="report-source-line">United Nations · January 2026</p>
        </FadeIn>

        <FadeIn delay={300}>
          <p className="report-body-text">
            In January 2026, the UN declared the world has entered an era of{" "}
            <strong className="report-highlight">
              global water bankruptcy
            </strong>{" "}
            — rivers, lakes, and aquifers depleted past the point of recovery.
            The old language of "water crisis" no longer applies. A crisis ends.
            This is permanent.
          </p>
        </FadeIn>

        <FadeIn delay={500}>
          <div className="report-stat-cards">
            {WATER_STATS.map(({ val, desc }) => (
              <div key={val} className="report-stat-card">
                <span className="report-stat-value">{val}</span>
                <span className="report-stat-desc">{desc}</span>
              </div>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={800}>
          <div className="report-quote-block">
            <p className="report-quote-text">
              And into this world, the AI industry is becoming one of the
              fastest growing new demands on disappearing water.
            </p>
            <ul className="report-bullet-list">
              {WATER_BULLETS.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        </FadeIn>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          What can I do?
        </button>
      </div>
    </div>
  );
}

const TIPS = [
  {
    num: "01",
    badge: "~54% less energy",
    title: "Ask for shorter answers",
    desc: 'Output tokens are where most compute goes. "Explain briefly" or "one paragraph max" cuts energy roughly in half.',
  },
  {
    num: "02",
    badge: "~100% saved",
    title: "Don't ask the same question twice",
    desc: "Copy your answer. Bookmark it. Screenshot it. The cheapest query is the one you never make.",
  },
  {
    num: "03",
    badge: "~10x less energy",
    title: "Do you need AI for this?",
    desc: "A web search uses roughly one tenth the energy. The AI summary at the top of Google uses up to 100x more energy than the links below it.",
  },
  {
    num: "04",
    badge: "~30–45% saved",
    title: "Batch your questions",
    desc: "Combining related questions into one prompt means one model initialization instead of four.",
  },
];

function ScreenTips({ onNext }) {
  const scrollRef = useRef(null);

  return (
    <div className="report-screen" ref={scrollRef}>
      <div className="report-inner">
        <FadeIn delay={0}>
          <p className="report-label">04 / 06</p>
        </FadeIn>

        <FadeIn delay={100}>
          <h1 className="report-hero-title">Things that actually help</h1>
          <p className="report-hero-sub">Honest actions with real impact</p>
        </FadeIn>

        <div className="report-tip-cards">
          {TIPS.map(({ num, badge, title, desc }, i) => (
            <FadeIn key={num} delay={300 + i * 120}>
              <div className="report-tip-card">
                <div className="report-tip-card-header">
                  <span className="report-tip-number">{num}</span>
                  <span className="report-tip-badge">{badge}</span>
                </div>
                <p className="report-tip-title">{title}</p>
                <p className="report-tip-desc">{desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          Now see what you can't control →
        </button>
      </div>
    </div>
  );
}

const CANT_CONTROL = [
  {
    title: "Which model runs your query",
    desc: "Decided by the company. Most platforms auto-select or hide model choice.",
  },
  {
    title: "Where the data center is",
    desc: "You can't choose. Over half are in water-stressed regions. The routing is invisible.",
  },
  {
    title: "Whether the industry grows",
    desc: "By 2030, US data centers will consume more electricity than all American manufacturing combined.",
  },
  {
    title: "Whether the water comes back",
    desc: "The aquifers being drawn down took thousands of years to fill. The UN says they won't recover in our lifetimes.",
  },
];

function ScreenCantControl({ onNext }) {
  const scrollRef = useRef(null);

  return (
    <div className="report-screen" ref={scrollRef}>
      <div className="report-inner">
        <FadeIn delay={0}>
          <p className="report-label">05 / 06</p>
        </FadeIn>

        <FadeIn delay={100}>
          <h1 className="report-hero-title">Things you can't control</h1>
        </FadeIn>

        <div className="report-cant-control-list">
          {CANT_CONTROL.map(({ title, desc }, i) => (
            <FadeIn key={title} delay={350 + i * 150}>
              <div className="report-cant-control-item">
                <p className="report-cant-control-title">{title}</p>
                <p className="report-cant-control-desc">{desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          So is it enough?
        </button>
      </div>
    </div>
  );
}

function ScreenGap({ onNext }) {
  const scrollRef = useRef(null);
  const [showExtra, setShowExtra] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowExtra(true), 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="report-screen report-screen--gap" ref={scrollRef}>
      <div className="report-gap-content">
        <FadeIn delay={0}>
          <p className="report-label report-label--center">06 / 06</p>
        </FadeIn>

        <FadeIn delay={200}>
          <div className="report-gap-block">
            <p className="report-gap-context">
              If every user followed every tip, perfectly
            </p>
            <p className="report-gap-stat">−54%</p>
            <p className="report-gap-sub">per query</p>
          </div>
        </FadeIn>

        <FadeIn delay={500}>
          <div className="report-gap-block">
            <p className="report-gap-context">
              Meanwhile, AI water consumption by 2028
            </p>
            <p className="report-gap-stat report-gap-stat--orange">+1,100%</p>
          </div>
        </FadeIn>

        <FadeIn delay={800}>
          <div className="report-gap-divider" />
          <p className="report-gap-closing">
            The question isn't how to use AI more efficiently.
            <br />
            It's who decides how much water a chatbot is worth.
          </p>
        </FadeIn>
      </div>

      <div className="report-bottom">
        <button className="report-bottom-button" onClick={onNext}>
          Ask Another Question
          <span
            className={`report-button-extra${
              showExtra ? " report-button-extra--visible" : ""
            }`}
          >
            ... or not?
          </span>
        </button>
      </div>
    </div>
  );
}

export default function ImpactReport({
  isOpen,
  onClose,
  energyJ = 0,
  question = "",
  modelUsed = "",
}) {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(1);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setScreen(1);
      setIsExiting(false);
    }
  }, [isOpen]);

  const navigateHome = () => {
    setIsExiting(true);
    setTimeout(() => navigate("/"), 500);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`report-overlay${isExiting ? " report-overlay--exit" : ""}`}
      onClick={onClose}
    >
      <div className="report-popup" onClick={(e) => e.stopPropagation()}>
        {screen === 1 && (
          <ScreenReceipt energyJ={energyJ} onNext={() => setScreen(2)} />
        )}
        {screen === 2 && <ScreenRightNow onNext={() => setScreen(3)} />}
        {screen === 3 && <ScreenWaterBankruptcy onNext={() => setScreen(4)} />}
        {screen === 4 && <ScreenTips onNext={() => setScreen(5)} />}
        {screen === 5 && <ScreenCantControl onNext={() => setScreen(6)} />}
        {screen === 6 && <ScreenGap onNext={navigateHome} />}
      </div>
    </div>
  );
}
